import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { Helmet } from "react-helmet";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../../api/axiosInstance";
import LogoWithHomeLink from "../../../components/LogoWithHomeLink";
import SitePageLeftHeading from "../../../components/SitePageLeftHeading";
import SiteRightContent from "../../../components/SiteRightContent";
import toast from "react-hot-toast";

export default function CustomerDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();

  const data = state?.data;

  const [optionsYears] = useState(() => {
    const currentYear = new Date().getFullYear();
    let yearStart = currentYear - 60;
    const yearEnd = currentYear - 18;

    let options = [];

    while (yearStart <= yearEnd) {
      options.push(yearStart++);
    }

    return options;
  });

  const [optionsDays] = useState(() => {
    let options = [];
    for (let i = 1; i <= 31; i++) {
      options.push(i);
    }

    return options;
  });

  const optionsMonths = [
    [1, t('months.jan')],
    [2, t('months.feb')],
    [3, t('months.mar')],
    [4, t('months.apr')],
    [5, t('months.may')],
    [6, t('months.jun')],
    [7, t('months.jul')],
    [8, t('months.aug')],
    [9, t('months.sep')],
    [10, t('months.oct')],
    [11, t('months.nov')],
    [12, t('months.dec')],
  ];

  // const setOptionsForDays = (e) => {
  //     let m = e.target.value
  //     let dayEnd = 30
  //     let options = [];

  //     if (!isNaN(m)){
  //         m = parseInt(m)

  //         if (m == 2) {
  //             if(initialValues.birthYear && !isNaN(initialValues.birthYear)){
  //                 dayEnd = (initialValues.birthYear % 4 == 0) ? 29 : 28
  //             }
  //         } else if (m == 1 || m == 3 || m == 5 || m == 7 || m == 8 || m == 10 || m == 12){
  //             dayEnd = 31
  //         }
  //     }

  //     for(let i = 1; i <= dayEnd; i++){
  //         options.push(i)
  //     }

  //     setOptionsDays(options)
  // }

  const validationSchema = Yup.object({
    firstName: Yup.string()
      .max(50, t("validation.max", { max: 50 }))
      .required(t("validation.required")),
    lastName: Yup.string()
      .max(50, t("validation.max", { max: 50 }))
      .required(t("validation.required")),
    otherNames: Yup.string().max(50, t("validation.max", { max: 50 })),
    address: Yup.string()
      .max(100, t("validation.max", { max: 100 }))
      .required(t("validation.required")),
    city: Yup.string()
      .max(50, t("validation.max", { max: 50 }))
      .required(t("validation.required")),
    idNumber: Yup.string()
      .max(12, t("validation.max", { max: 12 }))
      .required(t("validation.required")),
    birthYear: Yup.number(t("validation.number"))
      .integer()
      .positive()
      .required(t("validation.required")),
  });

  const initialValues = {
    firstName: "",
    lastName: "",
    otherNames: "",
    address: "",
    city: "",
    idNumber: "",
    birthYear: "",
    birthMonth: "",
    birthDay: "",
  };

  //   const optionsForYears = () => {
  //     const currentYear = new Date().getFullYear();
  //     let yearStart = currentYear - 60
  //     const yearEnd = currentYear - 18

  //     let options = [];

  //     while(yearStart <= yearEnd) {
  //         options.push(yearStart++)
  //     }

  //     return
  //   }

  const handleSubmit = async (values, { setFieldError, setSubmitting }) => {
    try {
      values.sessionId = data.sessionId;

      const response = await api.post(
        "/api/v1/registration/customer-detail",
        values
      );

      navigate("/registration/status", { state: { data: response.data } });
    } catch (error) {
      const resp = error.response?.data;

      if (resp?.message) {
        toast.error(resp.message);
      }
    }
    setSubmitting(false);
  };

  return (
    <>
      <Helmet>
        <title>{t("pages.registration.customer_detail.title")}</title>
      </Helmet>

      <div className="flex flex-col flex-1 w-full lg:w-1/2">
        <LogoWithHomeLink />

        <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
          <SitePageLeftHeading
            title={t("pages.registration.customer_detail.page_title")}
            description={t("pages.registration.customer_detail.description")}
          />

          <div className="space-y-5">
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {(props) => (
                <Form>
                  <div className="mb-2">
                    <label
                      htmlFor="idNumber"
                      className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400"
                    >
                      {t("pages.registration.customer_detail.form.id_number")}
                    </label>
                    <Field
                      type="text"
                      name="idNumber"
                      id="idNumber"
                      maxLength={12}
                      autoFocus
                      className={
                        (props.errors.idNumber
                          ? "border-error-300 focus:border-error-300 focus:ring-error-500/10 dark:border-error-700 dark:focus:border-error-800 "
                          : "border-gray-300 ") +
                        "dark:bg-dark-900 shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-base text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"
                      }
                    />
                    <ErrorMessage
                      name="idNumber"
                      component="p"
                      className="text-theme-xs text-error-500 mt-1.5"
                    />
                  </div>

                  <div className="mb-2">
                    <label
                      htmlFor="firstName"
                      className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400"
                    >
                      {t("pages.registration.customer_detail.form.first_name")}
                    </label>
                    <Field
                      type="text"
                      name="firstName"
                      id="firstName"
                      maxLength={50}
                      
                      className={
                        (props.errors.firstName
                          ? "border-error-300 focus:border-error-300 focus:ring-error-500/10 dark:border-error-700 dark:focus:border-error-800 "
                          : "border-gray-300 ") +
                        "dark:bg-dark-900 shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-base text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"
                      }
                    />
                    <ErrorMessage
                      name="firstName"
                      component="p"
                      className="text-theme-xs text-error-500 mt-1.5"
                    />
                  </div>

                  <div className="mb-2">
                    <label
                      htmlFor="lastName"
                      className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400"
                    >
                      {t("pages.registration.customer_detail.form.last_name")}
                    </label>
                    <Field
                      type="text"
                      name="lastName"
                      id="lastName"
                      maxLength={50}
                      
                      className={
                        (props.errors.lastName
                          ? "border-error-300 focus:border-error-300 focus:ring-error-500/10 dark:border-error-700 dark:focus:border-error-800 "
                          : "border-gray-300 ") +
                        "dark:bg-dark-900 shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-base text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"
                      }
                    />
                    <ErrorMessage
                      name="lastName"
                      component="p"
                      className="text-theme-xs text-error-500 mt-1.5"
                    />
                  </div>

                  <div className="mb-2">
                    <label
                      htmlFor="otherNames"
                      className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400"
                    >
                      {t("pages.registration.customer_detail.form.other_names")}
                    </label>
                    <Field
                      type="text"
                      name="otherNames"
                      id="otherNames"
                      maxLength={50}
                      
                      className={
                        (props.errors.otherNames
                          ? "border-error-300 focus:border-error-300 focus:ring-error-500/10 dark:border-error-700 dark:focus:border-error-800 "
                          : "border-gray-300 ") +
                        "dark:bg-dark-900 shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-base text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"
                      }
                    />
                    <ErrorMessage
                      name="otherNames"
                      component="p"
                      className="text-theme-xs text-error-500 mt-1.5"
                    />
                  </div>

                  <div className="mb-2">
                    <label
                      htmlFor="address"
                      className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400"
                    >
                      {t("pages.registration.customer_detail.form.address")}
                    </label>
                    <Field
                      type="text"
                      name="address"
                      id="address"
                      maxLength={100}
                      
                      className={
                        (props.errors.address
                          ? "border-error-300 focus:border-error-300 focus:ring-error-500/10 dark:border-error-700 dark:focus:border-error-800 "
                          : "border-gray-300 ") +
                        "dark:bg-dark-900 shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-base text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"
                      }
                    />
                    <ErrorMessage
                      name="address"
                      component="p"
                      className="text-theme-xs text-error-500 mt-1.5"
                    />
                  </div>

                  <div className="mb-2">
                    <label
                      htmlFor="city"
                      className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400"
                    >
                      {t("pages.registration.customer_detail.form.city")}
                    </label>
                    <Field
                      type="text"
                      name="city"
                      id="city"
                      maxLength={50}
                      
                      className={
                        (props.errors.city
                          ? "border-error-300 focus:border-error-300 focus:ring-error-500/10 dark:border-error-700 dark:focus:border-error-800 "
                          : "border-gray-300 ") +
                        "dark:bg-dark-900 shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-base text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"
                      }
                    />
                    <ErrorMessage
                      name="city"
                      component="p"
                      className="text-theme-xs text-error-500 mt-1.5"
                    />
                  </div>

                  <div className="mb-2">
                    <label
                      htmlFor="birthYear"
                      className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400"
                    >
                      {t(
                        "pages.registration.customer_detail.form.date_of_birth"
                      )}
                    </label>
                    <div className="flex flex-row">
                      <div className="mr-1">
                        <div className="relative z-20 bg-transparent">
                          <Field
                            as="select"
                            name="birthYear"
                            id="birthYear"
                            className={
                              (props.errors.birthYear
                                ? "border-error-300 focus:border-error-300 focus:ring-error-500/10 dark:border-error-700 dark:focus:border-error-800"
                                : "border-gray-300") +
                              " h-11 w-full appearance-none rounded-lg border dark:bg-dark-900 shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 bg-transparent bg-none px-4 py-2.5 pr-11 text-base text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"
                            }
                          >
                            <option
                              value=""
                              className="text-gray-700 dark:bg-gray-900 dark:text-gray-400"
                            >
                              {t('pages.registration.customer_detail.form.select.year')}
                            </option>
                            {optionsYears.map((year) => (
                              <option
                                key={year}
                                value={year}
                                className="text-gray-700 dark:bg-gray-900 dark:text-gray-400"
                              >
                                {year}
                              </option>
                            ))}
                          </Field>
                          <span className="pointer-events-none absolute top-1/2 right-4 z-30 -translate-y-1/2 text-gray-700 dark:text-gray-400">
                            <svg
                              className="stroke-current"
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M4.79175 7.396L10.0001 12.6043L15.2084 7.396"
                                stroke=""
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              />
                            </svg>
                          </span>
                        </div>
                        <ErrorMessage
                          name="birthYear"
                          component="p"
                          className="text-theme-xs text-error-500 mt-1.5"
                        />
                      </div>

                      <div className="mr-1">
                        <div className="relative z-20 bg-transparent">
                          <Field
                            as="select"
                            name="birthMonth"
                            id="birthMonth"
                            className={
                              (props.errors.birthMonth
                                ? "border-error-300 focus:border-error-300 focus:ring-error-500/10 dark:border-error-700 dark:focus:border-error-800"
                                : "border-gray-300") +
                              " h-11 w-full appearance-none rounded-lg border dark:bg-dark-900 shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 bg-transparent bg-none px-4 py-2.5 pr-11 text-base text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"
                            }
                          >
                            <option
                              value=""
                              className="text-gray-700 dark:bg-gray-900 dark:text-gray-400"
                            >
                              {t('pages.registration.customer_detail.form.select.month')}
                            </option>
                            {optionsMonths.map((month) => (
                              <option
                                key={month[0]} value={month[0]}
                                className="text-gray-700 dark:bg-gray-900 dark:text-gray-400"
                              >
                                {month[1]}
                              </option>
                            ))}
                          </Field>
                          <span className="pointer-events-none absolute top-1/2 right-4 z-30 -translate-y-1/2 text-gray-700 dark:text-gray-400">
                            <svg
                              className="stroke-current"
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M4.79175 7.396L10.0001 12.6043L15.2084 7.396"
                                stroke=""
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              />
                            </svg>
                          </span>
                        </div>
                        <ErrorMessage
                          name="birthMonth"
                          component="p"
                          className="text-theme-xs text-error-500 mt-1.5"
                        />
                      </div>

                      <div className="mr-1">
                        <div className="relative z-20 bg-transparent">
                          <Field
                            as="select"
                            name="birthDay"
                            id="birthDay"
                            className={
                              (props.errors.birthDay
                                ? "border-error-300 focus:border-error-300 focus:ring-error-500/10 dark:border-error-700 dark:focus:border-error-800"
                                : "border-gray-300") +
                              " h-11 w-full appearance-none rounded-lg border dark:bg-dark-900 shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 bg-transparent bg-none px-4 py-2.5 pr-11 text-base text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"
                            }
                          >
                            <option
                              value=""
                              className="text-gray-700 dark:bg-gray-900 dark:text-gray-400"
                            >
                              {t('pages.registration.customer_detail.form.select.day')}
                            </option>
                            {optionsDays.map((day) => (
                              <option
                                key={day} value={day}
                                className="text-gray-700 dark:bg-gray-900 dark:text-gray-400"
                              >
                                {day}
                              </option>
                            ))}
                          </Field>
                          <span className="pointer-events-none absolute top-1/2 right-4 z-30 -translate-y-1/2 text-gray-700 dark:text-gray-400">
                            <svg
                              className="stroke-current"
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M4.79175 7.396L10.0001 12.6043L15.2084 7.396"
                                stroke=""
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              />
                            </svg>
                          </span>
                        </div>
                        <ErrorMessage
                          name="birthDay"
                          component="p"
                          className="text-theme-xs text-error-500 mt-1.5"
                        />
                      </div>

                    </div>
                  </div>

                  <div className="my-6">
                    <button
                      type="submit"
                      disabled={props.isSubmitting}
                      className="flex items-center justify-center w-full px-4 py-3 text-sm font-medium text-white transition rounded-lg bg-brand-500 shadow-theme-xs hover:bg-brand-600"
                    >
                      {props.isSubmitting
                        ? t("buttons.submitting")
                        : t("buttons.submit")}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
      <SiteRightContent />
    </>
  );
}
